import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { Pencil } from "lucide-react";
import { useTenants, useCreateTenant, useUpdateTenant } from "@/api/tenants";
import { usePagination } from "@/hooks/use-pagination";
import { DataTable } from "@/components/data-table/data-table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { useAuth } from "@/stores/auth-store";
import { isSuperAdmin } from "@/lib/roles";
import type { Tenant } from "@/api/types";

const statusVariant: Record<string, "success" | "warning" | "danger"> = {
  active: "success",
  onboarding: "warning",
  inactive: "danger",
};

function canEditTenant(user: { role: string } | null): boolean {
  return !!user && (user.role === "super_admin" || user.role === "operator");
}

export function TenantListPage() {
  const { page, pageSize, search, params, setPage, setPageSize, setSearch } = usePagination();
  const { data, isLoading } = useTenants(params);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTenant, setEditTenant] = useState<Tenant | null>(null);
  const { user } = useAuth();

  const columns: ColumnDef<Tenant, unknown>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <Link to={`/tenants/${row.original.id}`} className="font-medium text-accent hover:underline">
          {row.original.name}
        </Link>
      ),
    },
    { accessorKey: "slug", header: "Slug" },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => <Badge variant={statusVariant[row.original.status]}>{row.original.status}</Badge>,
    },
    { accessorKey: "domain", header: "Domain" },
    { accessorKey: "primary_contact_email", header: "Contact" },
    ...(canEditTenant(user)
      ? [
          {
            id: "actions",
            header: "",
            cell: ({ row }: { row: { original: Tenant } }) => (
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditTenant(row.original);
                }}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            ),
          } as ColumnDef<Tenant, unknown>,
        ]
      : []),
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Tenants</h1>
        {isSuperAdmin(user) && <Button onClick={() => setDialogOpen(true)}>Create Tenant</Button>}
      </div>
      <DataTable
        columns={columns}
        data={data?.data ?? []}
        isLoading={isLoading}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search tenants..."
        page={page}
        pageSize={pageSize}
        totalPages={data?.meta?.totalPages ?? 1}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        total={data?.meta?.total}
      />
      <TenantFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
      <TenantFormDialog
        open={!!editTenant}
        onClose={() => setEditTenant(null)}
        tenant={editTenant ?? undefined}
      />
    </div>
  );
}

function TenantFormDialog({ open, onClose, tenant }: { open: boolean; onClose: () => void; tenant?: Tenant }) {
  const isEdit = !!tenant;
  const createTenant = useCreateTenant();
  const updateTenant = useUpdateTenant(tenant?.id ?? "");
  const mutation = isEdit ? updateTenant : createTenant;

  const emptyForm = { name: "", slug: "", domain: "", status: "onboarding" as Tenant["status"], primary_contact_email: "" };
  const [form, setForm] = useState(emptyForm);

  const fieldErrors = mutation.isError
    ? (mutation.error as { response?: { data?: { error?: { details?: Record<string, string[]> } } } })
        ?.response?.data?.error?.details ?? {}
    : {};
  const generalError = mutation.isError && Object.keys(fieldErrors).length === 0
    ? (mutation.error as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message || "Something went wrong. Please try again."
    : null;

  useEffect(() => {
    if (open && tenant) {
      setForm({
        name: tenant.name,
        slug: tenant.slug,
        domain: tenant.domain ?? "",
        status: tenant.status,
        primary_contact_email: tenant.primary_contact_email ?? "",
      });
    } else if (open) {
      setForm(emptyForm);
    }
  }, [open, tenant]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.reset();
    mutation.mutate(form, {
      onSuccess: () => {
        onClose();
        setForm(emptyForm);
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{isEdit ? "Edit Tenant" : "Create Tenant"}</DialogTitle>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Name</label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          {fieldErrors.name && <p className="mt-1 text-xs text-danger">{fieldErrors.name[0]}</p>}
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Slug</label>
          <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required />
          {fieldErrors.slug && <p className="mt-1 text-xs text-danger">{fieldErrors.slug[0]}</p>}
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Domain</label>
          <Input value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} />
          {fieldErrors.domain && <p className="mt-1 text-xs text-danger">{fieldErrors.domain[0]}</p>}
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Status</label>
          <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as Tenant["status"] })}>
            <option value="onboarding">Onboarding</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>
          {fieldErrors.status && <p className="mt-1 text-xs text-danger">{fieldErrors.status[0]}</p>}
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Contact Email</label>
          <Input type="email" value={form.primary_contact_email} onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })} />
          {fieldErrors.primary_contact_email && <p className="mt-1 text-xs text-danger">{fieldErrors.primary_contact_email[0]}</p>}
        </div>
        {generalError && <p className="text-sm text-danger">{generalError}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? (isEdit ? "Saving..." : "Creating...") : (isEdit ? "Save" : "Create")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
